// Digital Corporate Chef: Welcome Back pulse check.
//
// Triggered once per login (session-scoped flag set by Login.jsx). Fetches a
// short owner-report snapshot, distills one clinical sentence, and pops a
// glass card in the bottom right with two actions: Review Audit (opens the
// Chef consultation panel) and Dismiss. Not a tutorial. A five second pulse.

import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import AtomCore from "@/components/AtomCore";
import { X, ArrowRight } from "lucide-react";

const PENDING_KEY = "cs:welcome-back-pending";

// Turn an owner-report snapshot into one clinical, data-driven sentence.
// Rules: pick the sharpest signal, keep it short, zero em-dashes.
function buildPulseLine(report) {
  if (!report) return "The kitchen is quiet. No new signals since your last session.";

  const sales = report.sales || {};
  const totals = report.totals || {};
  const ded = report.deductions || {};
  const revenue = Number(sales.revenue || 0);
  const fcPct = Number(totals.food_cost_percent || 0);
  const compsLost = Number(ded.comps?.revenue_lost || 0);
  const waste = Number(ded.waste?.food_cost || 0);
  const staffMeals = Number(ded.staff_meals?.food_cost || 0);

  // Priority 1: food cost drift above 32 percent
  if (revenue > 0 && fcPct >= 32) {
    return `Food cost is running at ${fcPct.toFixed(1)} percent this week. That is above the fine dining threshold. Time for a pass.`;
  }

  // Priority 2: significant comps
  if (compsLost >= 100) {
    return `Comps have burned $${compsLost.toFixed(0)} in revenue this week. Worth a look at which dish keeps getting sent back.`;
  }

  // Priority 3: waste showing up
  if (waste >= 50) {
    return `Waste is trending at $${waste.toFixed(0)} in food cost this week. A short reformulation could shave that.`;
  }

  // Priority 4: heavy staff meal cost
  if (staffMeals >= 80) {
    return `Staff meals absorbed $${staffMeals.toFixed(0)} in food cost this week. That is bigger than average. Worth checking the family menu.`;
  }

  // Priority 5: quiet week, praise + soft signal
  if (revenue > 0) {
    return `Inventory is steady and food cost is holding at ${fcPct.toFixed(1)} percent. A quiet week. Good time to run a full margin audit.`;
  }

  // Fallback
  return "No sales recorded in the last week. When you post your first shift's sales, I will surface the sharpest signal for you.";
}


export default function WelcomeBackChef() {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [line, setLine] = useState("");

  const userId = user?.id;
  const firedRef = useRef(false);
  useEffect(() => {
    if (!userId) return;
    if (firedRef.current) return;
    let pending = false;
    try { pending = sessionStorage.getItem(PENDING_KEY) === "1"; } catch (_) { /* noop */ }
    if (!pending) return;
    firedRef.current = true;
    try { sessionStorage.removeItem(PENDING_KEY); } catch (_) { /* noop */ }

    (async () => {
      let report = null;
      try {
        const { data } = await api.get("/report/owner", { params: { days: 7 } });
        report = data;
      } catch (_) { /* silent */ }
      setLine(buildPulseLine(report));
      // Wait for the concierge carousel (if it opens) to be dismissed
      // before firing this welcome, so the two onboarding surfaces never
      // stack on top of each other.
      const showWhenClear = () => {
        const conciergeUp = !!document.querySelector('[data-testid="concierge-carousel"]');
        if (conciergeUp) {
          setTimeout(showWhenClear, 500);
          return;
        }
        setVisible(true);
      };
      setTimeout(showWhenClear, 600);
    })();
  }, [userId]);

  if (!visible) return null;

  const firstName = (user?.name || user?.email || "Boss").split(/[\s@]/)[0];
  const displayName = firstName ? firstName.charAt(0).toUpperCase() + firstName.slice(1) : "Boss";

  const reviewAudit = () => {
    setVisible(false);
    window.dispatchEvent(new CustomEvent("cs:open-corporate-chef", {
      detail: { seedMessage: "Run the pulse check audit for me right now." },
    }));
  };

  return (
    <div
      className="fixed z-[75] right-4 lg:right-6 bottom-40 lg:bottom-44 w-[92vw] max-w-[380px] rounded-2xl p-4"
      style={{
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.05) 55%, rgba(255,255,255,0.02) 100%)",
        backdropFilter: "blur(38px) saturate(180%)",
        WebkitBackdropFilter: "blur(38px) saturate(180%)",
        border: "1px solid rgba(212,175,55,0.5)",
        boxShadow: [
          "inset 0 1px 0 rgba(255,255,255,0.24)",
          "0 0 20px #d4af3740",
          "0 26px 60px -18px rgba(0,0,0,0.85)",
        ].join(","),
        animation: "welcome-back-in 520ms cubic-bezier(0.2, 0.85, 0.3, 1.1) both",
      }}
      role="dialog"
      aria-label="Digital Corporate Chef welcome back"
      data-testid="welcome-back-card"
    >
      {/* Prism hairline */}
      <div
        aria-hidden
        className="absolute top-0 left-4 right-4 h-px rounded-full pointer-events-none"
        style={{
          background:
            "linear-gradient(90deg, transparent, #D4AF37, #FFF8E1, #D4AF37, transparent)",
        }}
      />

      <button
        onClick={() => setVisible(false)}
        aria-label="Dismiss welcome"
        data-testid="welcome-back-close"
        className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center"
        style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.10)" }}
      >
        <X className="w-3.5 h-3.5 text-white/65" />
      </button>

      <div className="flex items-start gap-3 pr-6">
        <div
          className="shrink-0 rounded-xl flex items-center justify-center"
          style={{
            width: 44,
            height: 44,
            background: "radial-gradient(circle at 30% 30%, rgba(255,248,225,0.18), rgba(212,175,55,0.05) 65%, transparent 100%)",
            border: "1px solid rgba(212,175,55,0.55)",
            boxShadow: "0 0 20px #d4af3740, inset 0 1px 0 rgba(255,255,255,0.28)",
          }}
        >
          <AtomCore size={30} testId="welcome-back-atom" />
        </div>
        <div className="flex-1 min-w-0">
          <div
            className="font-display text-[1.05rem] leading-tight"
            style={{ textShadow: "0 0 14px rgba(212,175,55,0.5), 0 0 3px rgba(255,248,225,0.35)" }}
            data-testid="welcome-back-hello"
          >
            Welcome back, {displayName}.
          </div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/50 mt-0.5">
            Digital Corporate Chef
          </div>
        </div>
      </div>

      <p
        className="text-[0.9rem] text-white/85 leading-relaxed mt-3"
        data-testid="welcome-back-pulse"
      >
        {line}
      </p>

      <div className="flex items-center gap-2 mt-4">
        <button
          onClick={reviewAudit}
          data-testid="welcome-back-review"
          className="flex-1 inline-flex items-center justify-center gap-2 rounded-full font-semibold text-[0.85rem] px-4 py-2 transition-transform hover:scale-[1.02]"
          style={{
            background:
              "linear-gradient(180deg, rgba(212,175,55,0.85), rgba(200,169,106,0.5))",
            border: "1px solid rgba(255,248,225,0.55)",
            color: "#1c1305",
            boxShadow:
              "inset 0 1px 0 rgba(255,255,255,0.55), 0 0 20px #d4af3740",
          }}
        >
          Review Audit
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => setVisible(false)}
          data-testid="welcome-back-dismiss"
          className="inline-flex items-center justify-center rounded-full font-medium text-[0.85rem] px-4 py-2 transition-colors"
          style={{
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.12)",
            color: "rgba(255,255,255,0.78)",
          }}
        >
          Dismiss
        </button>
      </div>

      <style>{`
        @keyframes welcome-back-in {
          0%   { opacity: 0; transform: translateY(18px) scale(0.96); }
          70%  { opacity: 1; transform: translateY(-3px) scale(1.01); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}
